import type { Connotations } from "@neru/types";
import { changeZIndex, findClosestElementWithClass } from "@neru/utils";
import {
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { useKeyboardNavigation } from "../../hooks/keyboardNavigation";
import {
  getDropdownHorizontalPosition,
  getDropdownVerticalPosition,
} from "../../utils";
import { type BasicButtonProps, Button } from "../Button/Button";
import { Icon, type IconType } from "../Icon/Icon";
import { OnBlurDetector } from "../OnBlurDetector/OnBlurDetector";
import { Spinner } from "../Progress/Spinner";

export interface MenuMethods {
  close: () => void;
}

export type MenuItem<T> = {
  action: T;
  connotation?: Connotations;
  icon?: IconType;
  promise?: boolean;
  title: string;
};

export interface ButtonWithLabel {
  icon?: IconType;
  label: string;
}

export interface ButtonWithIcon {
  icon: IconType;
  label?: never;
}

type ButtonProps =
  | (BasicButtonProps & ButtonWithIcon)
  | (BasicButtonProps & ButtonWithLabel);

export interface WithChildren {
  children: React.ReactNode;
  menuItems?: never;
  onAction?: never;
}

export interface WithMenuItems<T> {
  children?: never;
  menuItems: Array<MenuItem<T>>;
  onAction?: (value: T) => void | Promise<void>;
}

export type MenuProps<T> = WithChildren | WithMenuItems<T>;

interface RenderMenuItemsParams<T> {
  focusedIndex: number | null;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLElement>,
    handleAction: () => void | Promise<void>,
  ) => void;
  handler: (param: T, promise?: boolean) => void | Promise<void>;
  items: Array<MenuItem<T>>;
  testId?: string;
}

export const listItemStyles = `
    flex items-center cursor-pointer whitespace-nowrap select-none relative
    py-3 px-5 hover:bg-primary-50 focus:outline-none focus:bg-primary-50 
    dark:hover:bg-primary-900 dark:focus:bg-primary-900 dark:hover:text-primary-0 z-50 max-h-10
    border-0 w-full text-left font-inherit bg-transparent
`;

export const renderMenuItems = <T extends string>({
  focusedIndex,
  handleKeyDown,
  handler,
  items,
  testId = "menu",
}: RenderMenuItemsParams<T>) => (
  <nav
    className={`
      bg-primary-0 text-primary-1000
      dark:bg-primary-800 dark:text-primary-0
    `}
  >
    {items?.map(({ action, connotation, icon, promise, title }, index) => {
      const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handler(action, promise);
      };

      const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleKeyDown(e, () => handler(action, promise));
      };

      return (
        <button
          className={listItemStyles}
          data-testid={`${testId}-${title}`}
          disabled={Boolean(promise)}
          key={title}
          onClick={onClick}
          onKeyDown={onKeyDown}
          ref={(el) => {
            if (focusedIndex === index) {
              el?.focus();
            }
          }}
          type="button"
        >
          {icon && !promise && (
            <Icon
              className="mr-4"
              connotation={connotation}
              size="small"
              type={icon}
            />
          )}
          {promise ? (
            <div className="mr-4">
              <Spinner size={-6} />
            </div>
          ) : null}
          {title}
        </button>
      );
    })}
  </nav>
);

export const MenuWrapper = <T extends string>(
  {
    children,
    className = "",
    "data-testid": testId = "menu",
    menuItems = [],
    onAction,
    onClick,
    ...buttonProps
  }: MenuProps<T> & ButtonProps,
  ref: Ref<MenuMethods>,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { focusedIndex, handleKeyDown } = useKeyboardNavigation(
    menuRef.current,
  );

  const onMenuHandler = (value: T, promise?: boolean) => {
    if (onAction) {
      onAction(value);

      if (promise === undefined) {
        setIsOpen(false);
      }
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      close: () => {
        setIsOpen(false);
      },
    }),
    [],
  );

  useEffect(() => {
    const dropdown = menuRef.current;
    if (!dropdown) {
      return;
    }

    const liftableElement = findClosestElementWithClass(
      dropdown,
      "menu-lift-up",
    );

    if (isOpen) {
      changeZIndex(liftableElement, 1);
    } else {
      changeZIndex(liftableElement, -1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const container = containerRef.current;
    const dropdown = menuRef.current;
    if (!container || !dropdown) {
      return;
    }

    const setPosition = () => {
      const horizontalExpandTo = getDropdownHorizontalPosition(dropdown, {
        position: "right",
      });

      const verticalExpandTo = getDropdownVerticalPosition(dropdown, {
        position: "bottom",
      });

      const dropdownWidth = dropdown.clientWidth;
      const dropdownHeight = dropdown.clientHeight;

      const containerRect = container.getBoundingClientRect();

      const left =
        horizontalExpandTo === "left" ? containerRect.width - dropdownWidth : 0;
      const top =
        verticalExpandTo === "top" ? -dropdownHeight : containerRect.height;

      const positionStyles = {
        left: `${left}px`,
        top: `${top}px`,
        transformOrigin: `${verticalExpandTo === "top" ? "bottom" : "top"} ${
          horizontalExpandTo === "left" ? "right" : "left"
        }`,
      };

      dropdown.dataset["positioned"] = "true";
      Object.assign(dropdown.style, positionStyles);
    };

    const resizeObserver = new ResizeObserver(() => {
      setPosition();
    });

    resizeObserver.observe(dropdown);

    return () => {
      if (dropdown) {
        resizeObserver.unobserve(dropdown);
      }
    };
  }, [isOpen]);

  return (
    <OnBlurDetector
      onBlur={() => {
        setIsOpen(false);
      }}
    >
      <div className="relative mx-px" data-testid={testId} ref={containerRef}>
        <Button
          {...buttonProps}
          className="min-w-full"
          data-testid={`${testId}-button`}
          onClick={(e) => {
            e.stopPropagation();
            onClick && onClick(e);
            toggleMenu();
          }}
          onKeyDown={(e) => handleKeyDown(e, toggleMenu)}
        />
        <div
          className={`
            drop-shadow-base absolute z-50 overflow-hidden rounded-md text-sm
          `}
          data-testid={`${testId}-menu`}
          ref={menuRef}
          style={{ display: isOpen ? "block" : "none" }}
        >
          {children ||
            renderMenuItems({
              focusedIndex,
              handleKeyDown,
              handler: onMenuHandler,
              items: menuItems,
              testId,
            })}
        </div>
      </div>
    </OnBlurDetector>
  );
};

export const Menu = forwardRef(MenuWrapper) as <T extends string>(
  props: MenuProps<T> & ButtonProps & { ref?: Ref<MenuMethods> },
) => ReturnType<typeof MenuWrapper>;
